/**
 * Read-only detail view for one stored Explore trend: its per-source
 * metadata and the underlying query/event/unclassified records with their
 * source, collection time, classification and expiry. Each record list
 * pages server-side ("Load more"); the first page arrives with the trend.
 */

import { useEffect, useState } from "react";
import { ArrowSquareOut as ExternalLink } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { formatDateTime } from "@/components/explore/format";
import {
  useExploreTrendDetail,
  useExploreTrendRecords,
} from "@/hooks/useExploreAdmin";
import { getErrorMessage } from "@/services/api";
import { EXPLORE_SOURCES, exploreSourceFullLabel } from "@/services/explore";
import type {
  AdminExploreTrend,
  ExploreRecordPage,
  ExploreRecordType,
  ExploreTrendRecord,
} from "@/services/explore";
import { cn } from "@/utils/cn";

const TABS: { type: ExploreRecordType; label: string }[] = [
  { type: "query", label: "Queries" },
  { type: "event", label: "Events" },
  { type: "unclassified", label: "Unclassified" },
];

function RecordRow({ record }: { record: ExploreTrendRecord }) {
  return (
    <li className="space-y-1 px-3 py-2.5">
      <p className="break-words text-sm">{record.text}</p>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <Badge variant="outline">
          {record.source === "x" ? "X" : "Google"}
        </Badge>
        {record.classificationMethod && (
          <span>classified by {record.classificationMethod}</span>
        )}
        <span>· collected {formatDateTime(record.fetchedAt)}</span>
        {record.expired && <Badge variant="warning">Expired</Badge>}
        {record.sourceUrl && (
          <a
            href={record.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-primary hover:underline"
          >
            source <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </li>
  );
}

function RecordList({
  trendId,
  type,
  firstPage,
}: {
  trendId: string;
  type: ExploreRecordType;
  firstPage: ExploreRecordPage;
}) {
  const [extra, setExtra] = useState<ExploreTrendRecord[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  // The parent keys this component by trend + tab, so state resets on switch.
  const next = useExploreTrendRecords(trendId, { type, cursor });

  const lastPage: ExploreRecordPage = cursor
    ? (next.data ?? { items: [], hasMore: false })
    : firstPage;
  const items = [
    ...firstPage.items,
    ...extra,
    ...(cursor ? lastPage.items : []),
  ];

  function loadMore() {
    if (cursor && next.data) setExtra((prev) => [...prev, ...next.data.items]);
    setCursor(lastPage.cursor);
  }

  if (items.length === 0 && !next.isLoading) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        No {type === "unclassified" ? "unclassified" : `${type}`} records stored
        for this trend.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
        {items.map((record) => (
          <RecordRow key={`${record.source}-${record.id}`} record={record} />
        ))}
      </ul>
      {next.error && (
        <p className="text-sm text-destructive">
          {getErrorMessage(next.error, "Failed to load more records.")}
        </p>
      )}
      {next.isLoading && cursor ? (
        <div className="flex justify-center py-2">
          <Spinner size="sm" />
        </div>
      ) : (
        lastPage.hasMore &&
        lastPage.cursor && (
          <Button variant="outline" size="sm" onClick={loadMore}>
            Load more
          </Button>
        )
      )}
    </div>
  );
}

export function TrendDetailDialog({
  trend,
  onOpenChange,
}: {
  trend: AdminExploreTrend | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, error } = useExploreTrendDetail(trend?.id);
  const [tab, setTab] = useState<ExploreRecordType>("query");
  const shown = data?.trend ?? trend;

  useEffect(() => setTab("query"), [trend?.id]);

  const pages: Record<ExploreRecordType, ExploreRecordPage | undefined> = {
    query: data?.queries,
    event: data?.events,
    unclassified: data?.unclassified,
  };
  const page = pages[tab];

  return (
    <Dialog open={trend !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="break-words">
            {shown?.title ?? "Trend"}
          </DialogTitle>
          <DialogDescription>
            {shown ? `${shown.topic} › ${shown.subtopic}` : "Trend details"}
          </DialogDescription>
        </DialogHeader>

        {shown && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-1.5">
              <Badge
                variant={shown.freshness === "fresh" ? "success" : "outline"}
              >
                {shown.freshness === "fresh" ? "Fresh" : "Expired"}
              </Badge>
              {shown.feedEligible && (
                <Badge variant="primary">Feed-eligible</Badge>
              )}
              {shown.sources.map((source) => (
                <Badge key={source} variant="outline">
                  {source === "x" ? "X" : "Google"}
                </Badge>
              ))}
            </div>
            {shown.snippet && (
              <p className="text-sm text-muted-foreground">{shown.snippet}</p>
            )}
            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
              <div>
                <dt className="inline">Popularity: </dt>
                <dd className="inline text-foreground">{shown.popularity}</dd>
              </div>
              <div>
                <dt className="inline">First seen: </dt>
                <dd className="inline text-foreground">
                  {formatDateTime(shown.firstSeenAt)}
                </dd>
              </div>
              <div>
                <dt className="inline">Last updated: </dt>
                <dd className="inline text-foreground">
                  {formatDateTime(shown.fetchedAt)}
                </dd>
              </div>
              <div>
                <dt className="inline">Expires: </dt>
                <dd className="inline text-foreground">
                  {formatDateTime(shown.expiresAt)}
                </dd>
              </div>
              {/* One line per PROVIDER that found this trend: admins need to
                  see which of Google's three collected it, and when. */}
              {EXPLORE_SOURCES.map((source) => {
                const meta = shown.sourceMetadata?.[source];
                if (!meta) return null;
                return (
                  <div key={source} className="sm:col-span-2">
                    <dt className="inline font-medium">
                      {exploreSourceFullLabel(source)}:{" "}
                    </dt>
                    <dd className="inline text-foreground">
                      {meta.signalType ? `${meta.signalType}, ` : ""}
                      classified by {meta.classificationMethod ?? "—"},
                      collected {formatDateTime(meta.fetchedAt)}
                      {meta.popularity !== undefined
                        ? `, popularity ${meta.popularity}`
                        : ""}
                      {meta.sourceUrl && (
                        <>
                          {" "}
                          <a
                            href={meta.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            open
                          </a>
                        </>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        )}

        <div className="flex gap-2 border-b border-border" role="tablist">
          {TABS.map(({ type, label }) => (
            <button
              key={type}
              role="tab"
              aria-selected={tab === type}
              onClick={() => setTab(type)}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                tab === type
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : error ? (
          <p className="text-sm text-destructive">
            {getErrorMessage(error, "Failed to load this trend.")}
          </p>
        ) : page && trend ? (
          <RecordList
            key={`${trend.id}-${tab}`}
            trendId={trend.id}
            type={tab}
            firstPage={page}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
